// src/components/admin/TorreDeControl/modals/DriverModal.jsx
import React, { useState, useEffect } from 'react';
import { X, Truck, Search } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

function DriverModal({ item, itemType, onClose, onUpdate }) {
  const [drivers, setDrivers] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState(item.driver_id || '');
  const [sequence, setSequence] = useState(item.sequence || '');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingDrivers, setIsFetchingDrivers] = useState(true);

  useEffect(() => {
    fetchDrivers();
  }, []);

  async function fetchDrivers() {
    setIsFetchingDrivers(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/drivers`);
      setDrivers(response.data.drivers || []);
    } catch (error) {
      console.error('Error fetching drivers:', error);
      alert('Error al cargar motoristas');
    } finally {
      setIsFetchingDrivers(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setIsLoading(true);

    try {
      const endpoint =
        itemType === 'delivery'
          ? `${API_URL}/api/admin/delivery-point/${item.id}/driver`
          : `${API_URL}/api/admin/order/${item.id}/driver`;

      await axios.put(endpoint, {
        driver_id: selectedDriverId || null,
        sequence: sequence || null,
      });
      
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error updating driver:', error);
      alert('Error al asignar motorizado');
    } finally {
      setIsLoading(false);
    }
  }

  const filteredDrivers = drivers.filter((driver) =>
    driver.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    driver.id.toString().includes(searchTerm)
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[calc(100dvh-1rem)] sm:max-h-[90dvh] min-h-0 overflow-hidden flex flex-col">
        <div className="bg-[#B1336E] px-3 py-3 sm:px-6 sm:py-4 flex items-center justify-between gap-2 shrink-0">
          <h3 className="text-white font-bold text-lg flex items-center gap-2 font-montserrat">
            <Truck className="w-5 h-5" />
            Asignar Motorizado
          </h3>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="min-h-0 flex-1 overflow-y-auto p-3 sm:p-6 space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar motorizado..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] font-montserrat"
            />
          </div>

          {/* Driver List */}
          <div className="max-h-64 overflow-y-auto space-y-2 border border-slate-200 rounded-lg p-2">
            {isFetchingDrivers ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#B1336E] mx-auto"></div>
                <p className="mt-2 text-sm text-slate-600 font-montserrat">Cargando motoristas...</p>
              </div>
            ) : filteredDrivers.length === 0 ? (
              <p className="text-center py-8 text-sm text-slate-500 font-montserrat">
                No se encontraron motoristas
              </p>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setSelectedDriverId('')}
                  className={`w-full text-left p-3 rounded-lg transition-all font-montserrat ${
                    selectedDriverId === ''
                      ? 'bg-[#B1336E] text-white'
                      : 'bg-white hover:bg-slate-50 border border-slate-200'
                  }`}
                >
                  <p className="font-medium">Sin asignar</p>
                  <p className="text-xs opacity-75">Remover motorizado actual</p>
                </button>
                
                {filteredDrivers.map((driver) => (
                  <button
                    key={driver.id}
                    type="button"
                    onClick={() => setSelectedDriverId(driver.id)}
                    className={`w-full text-left p-3 rounded-lg transition-all font-montserrat ${
                      selectedDriverId === driver.id
                        ? 'bg-[#B1336E] text-white'
                        : 'bg-white hover:bg-slate-50 border border-slate-200'
                    }`}
                  >
                    <p className="font-medium">{driver.username}</p>
                    <p className="text-xs opacity-75">ID: {driver.id}</p>
                    {driver.phone_number && (
                      <p className="text-xs opacity-75">{driver.phone_number}</p>
                    )}
                  </button>
                ))}
              </>
            )}
          </div>

          {/* Sequence */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2 font-montserrat">
              Secuencia (opcional)
            </label>
            <input
              type="number"
              value={sequence}
              onChange={(e) => setSequence(e.target.value)}
              placeholder="Orden de entrega"
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] font-montserrat"
            />
          </div>

          <div className="flex gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-slate-200 hover:bg-slate-300 text-slate-700 px-4 py-2 rounded-lg font-medium transition-colors font-montserrat"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 bg-[#B1336E] hover:bg-[#B1336E]/90 disabled:bg-slate-300 text-white px-4 py-2 rounded-lg font-medium transition-colors font-montserrat"
            >
              {isLoading ? 'Guardando...' : 'Asignar Motorizado'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DriverModal;
